import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/font-faces.css';
import './assets/watchmen-theme.css';
import './assets/theme-tokens.css';
import './styles/tokens-v2.css';       // DESIGN-FOUNDATION-1: enterprise token layer (additive)
import './styles/tokens-v1-shim.css';  // DESIGN-FOUNDATION-1: backwards-compat shadow aliases
import './assets/portal-theme.css';  /* BRAND-BOOK-RECONCILIATION-1: portal cascade isolated — loads last, always wins */
import './layouts/grid.css';          // DESIGN-FOUNDATION-5: 12-column grid utilities
import './assets/reset-tabular-nums.css';
import './styles/tabular-numerics.css'; // TMS-E2-TABULAR-NUMS-1: global td/th tabular-nums + .kr-numeric utility
import './assets/mode-color-tokens.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './store/themeStore'; // side-effect: applies theme from localStorage on load
import './i18n'; // side-effect: initializes i18next with en bundles (I18N-SUBSTRATE-1)
import './styles/rtl.css'; // RTL-FOUNDATION-1
import './styles/intl-responsive.css'; // INTL-MOBILE-RESPONSIVE-1: EC-1 §2.11 hscroll unblock
import './services/connectors'; // side-effect: registers N4 connectors with IntegrationService
import { bootstrapHal } from './services/iot/halBootstrap.js';
import { subscribeToYardGateEvents } from './services/pylon/index.js';
import { loadSeedData, loadCxSeed, isCxSeedLoaded } from './data/seedLoader';
import { configureCapabilityResolver } from './platform/hasCapability';
import { configureTenantResolver as configureCapabilityServiceTenantResolver } from './services/CapabilityService';
import useAppStore from './store/appStore';
import { getCurrentTenantId } from './services/TenantResolverService';

// CRUISE-NCL-TENANT-1: resolve tenantId from hostname before React mounts.
// Every service and component reads from this singleton — never re-resolved mid-session.
window.__KAIROS_TENANT_ID__ = getCurrentTenantId();

configureCapabilityResolver(() => useAppStore.getState().tenantId);
configureCapabilityServiceTenantResolver(() => useAppStore.getState().tenantId);

bootstrapHal();
subscribeToYardGateEvents();

// CRM-PERFORMANCE-BASELINE-1: wire Web Vitals RUM pipeline (fire-and-forget).
import('./services/webVitalsInit.js').catch(() => {});

// Populate localStorage with expanded seed data on first load
loadSeedData();

// Populate CX foundation data (cx_cases, cx_templates, customer_contacts, cx_messages, surveys)
if (!isCxSeedLoaded()) {
  loadCxSeed().catch(e => console.warn('[main] CX seed failed:', e?.message));
}

// Global styles
const globalStyles = document.createElement('style');
globalStyles.textContent = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { font-size: 16px; -webkit-font-smoothing: antialiased; }
  body { font-family: var(--font-body); overflow: hidden; height: 100vh; }
  a { text-decoration: none; color: inherit; }
  button { cursor: pointer; font-family: inherit; }
  input, select, textarea { font-family: inherit; outline: none; }
`;
document.head.appendChild(globalStyles);

// STALE-CHUNK-RECOVERY-1 + STALE-DEPLOY-RECOVERY-1: detect deploy-while-open
// chunk failures and reload.
//
// STALE-CHUNK-RECOVERY-1 installs window-level `error`/`unhandledrejection`
// handlers that fire when a stale chunk rejection escapes React. In practice
// React's ErrorBoundary catches lazy-import rejections first (Suspense turns
// them into render-time throws), so STALE-DEPLOY-RECOVERY-1 adds
// `lib/lazyWithRetry.js` which intercepts the same predicate at the
// promise level *before* React.lazy sees the rejection.
//
// Both paths share the predicate (`lib/staleChunkPredicate.js`) and the
// same sessionStorage TTL key below, so they count against one anti-loop
// budget. The window listener remains as the last line of defense for any
// non-React-routed stale-chunk error (icon imports, plugin chunks, etc.).
import { isStaleChunkError } from './lib/staleChunkPredicate.js';

const STALE_CHUNK_RELOAD_KEY = 'kairos:stale-chunk-reload-at';
const STALE_CHUNK_RELOAD_TTL_MS = 60_000;

function recoverFromStaleChunk(source) {
  const appName = window.__KAIROS_TENANT_ID__ === 'ncl' ? 'NCL Logistics Control Tower' : 'Kairos';
  const last = parseInt(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) || '0', 10);
  const now = Date.now();

  if (now - last < STALE_CHUNK_RELOAD_TTL_MS) {
    // Already reloaded once this session — don't loop. Show manual-refresh UI.
    document.body.innerHTML = `
      <div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Geist Variable','Geist',sans-serif;background:var(--white);color:var(--kairos-navy);text-align:center;padding:24px">
        <div>
          <div style="font-family:Cinzel,serif;font-size:28px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:16px">Update Required</div>
          <p style="font-size:15px;line-height:1.6;color:var(--kairos-navy);margin-bottom:24px;max-width:420px">${appName} was updated while you were using it. Please refresh to load the latest version.</p>
          <button onclick="sessionStorage.removeItem('${STALE_CHUNK_RELOAD_KEY}');location.reload()" style="padding:10px 24px;background:var(--gold-elyon);color:var(--white);font-weight:600;border:0;border-radius:4px;cursor:pointer;font-family:inherit;font-size:14px">Refresh</button>
        </div>
      </div>`;
    return;
  }

  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  console.warn('[StaleChunkRecovery] Detected stale chunk:', source, '— reloading to fetch fresh index.html');

  document.body.innerHTML = `
    <div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Geist Variable','Geist',sans-serif;background:var(--white);color:var(--kairos-navy);text-align:center">
      <div>
        <div style="font-family:Cinzel,serif;font-size:24px;letter-spacing:0.05em;text-transform:uppercase">Updating ${appName}…</div>
      </div>
    </div>`;

  setTimeout(() => window.location.reload(), 400);
}

window.addEventListener('error', (e) => {
  if (isStaleChunkError(e.message)) recoverFromStaleChunk(e.message);
});

window.addEventListener('unhandledrejection', (e) => {
  if (isStaleChunkError(e.reason)) recoverFromStaleChunk(e.reason);
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
